const UNITS = ["KB", "MB", "GB", "TB"] as const;

const decimals = new Map<number, Intl.NumberFormat>();
const numberFormat = (fractionDigits: number) => {
  let format = decimals.get(fractionDigits);
  if (!format) {
    format = new Intl.NumberFormat(undefined, { maximumFractionDigits: fractionDigits });
    decimals.set(fractionDigits, format);
  }
  return format;
};

export const formatBytes = (bytes: number): string => {
  if (bytes < 1000) return `${numberFormat(0).format(bytes)} ${bytes === 1 ? "byte" : "bytes"}`;
  let value = bytes / 1000;
  let unit = 0;
  while (value >= 999.95 && unit < UNITS.length - 1) {
    value /= 1000;
    unit += 1;
  }
  return `${numberFormat(value < 100 ? 1 : 0).format(value)} ${UNITS[unit]}`;
};

export const formatExactBytes = (bytes: number): string =>
  `${numberFormat(0).format(bytes)} ${bytes === 1 ? "byte" : "bytes"}`;

export const formatNumber = (value: number): string => numberFormat(0).format(value);

const pad = (value: number) => numberFormat(0).format(value).padStart(2, "0");

/** Clock reading for a media length: 4:07, 1:12:30. */
export const formatDuration = (seconds: number): string => {
  const whole = Math.round(seconds);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const rest = whole % 60;
  if (hours > 0) return `${numberFormat(0).format(hours)}:${pad(minutes)}:${pad(rest)}`;
  return `${numberFormat(0).format(minutes)}:${pad(rest)}`;
};

export const formatCount = (count: number, one: string, many: string): string =>
  `${numberFormat(0).format(count)} ${count === 1 ? one : many}`;

const money = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" });
const unitPrice = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
});

export const formatUsd = (amount: number): string => money.format(amount);

export const formatUnitPrice = (amount: number): string => unitPrice.format(amount);

const timeOnly = new Intl.DateTimeFormat(undefined, { timeStyle: "short" });
const dayMonth = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" });
const fullDay = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });
const dateTime = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });

export const formatShortDate = (iso: string, now = new Date()): string => {
  const date = new Date(iso);
  if (date.toDateString() === now.toDateString()) return timeOnly.format(date);
  if (date.getFullYear() === now.getFullYear()) return dayMonth.format(date);
  return fullDay.format(date);
};

export const formatTime = (iso: string): string => timeOnly.format(new Date(iso));

const weekday = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  day: "numeric",
  month: "short",
});
const weekdayYear = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
});

/** Local day with its weekday: Wed, 3 Sep. */
export const formatWeekday = (date: Date, withYear: boolean): string =>
  (withYear ? weekdayYear : weekday).format(date);

export const formatDate = (iso: string): string => fullDay.format(new Date(iso));

export const formatDateTime = (iso: string): string => dateTime.format(new Date(iso));

export const formatPercent = (fraction: number): string =>
  new Intl.NumberFormat(undefined, { style: "percent", maximumFractionDigits: 0 }).format(fraction);

/** A share that rounds to nothing still reads as more than nothing. */
export const formatShare = (fraction: number): string =>
  fraction > 0 && fraction < 0.005 ? "<1%" : formatPercent(fraction);

const monthLong = new Intl.DateTimeFormat(undefined, {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});
const monthShort = new Intl.DateTimeFormat(undefined, {
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});
const dayLong = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});
const dayShort = new Intl.DateTimeFormat(undefined, { day: "numeric", timeZone: "UTC" });

const utcOf = (value: string) => new Date(value.length === 7 ? `${value}-01T00:00:00Z` : value);

export const formatMonth = (value: string): string => monthLong.format(utcOf(value));

export const formatMonthShort = (value: string): string => monthShort.format(utcOf(value));

export const formatDay = (day: string): string => dayLong.format(utcOf(`${day}T00:00:00Z`));

export const formatDayNumber = (day: string): string => dayShort.format(utcOf(`${day}T00:00:00Z`));
